// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {Icon, Space, Styled, Popover, DateInput} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {
  useDebouncedCallback,
  useEffect,
  useResponsive,
  usePopover,
  useState,
  SetStateType,
  ResponsiveType,
} from '@supermove/hooks';
import {Organization, UserModel} from '@supermove/models';
import {Datetime} from '@supermove/utils';

// App
import SecondaryButton from '@shared/design/components/Button/SecondaryButton';
import ContextSwitcher from '@shared/design/components/ContextSwitcher';
import FieldInput from '@shared/design/components/Field/FieldInput';
import MultiDropdownCheckboxInput from '@shared/design/components/Field/MultiDropdownCheckboxInput';
import SearchBar from '@shared/design/components/SearchBar';
import Text from '@shared/design/components/Text';
import PopoverFilterWithFooter from '@shared/modules/App/components/PopoverFilterWithFooter';
import {
  SalesLeadsFiltersType,
  SalesLeadsFilterKeys,
  SalesLeadsTabKinds,
} from '@shared/modules/Sales/components/SalesLeads';
import UserRole from '@shared/modules/User/enums/UserRole';

const Row = Styled.View`
  flex-direction: row;
  align-items: center;
`;

const Container = Styled.View`
`;

const RowFilterInputContainer = Styled.View`
  flex: 1;
`;

const Section = Styled.View<{index: number}>`
  z-index: ${({index}) => 100 - index};
`;

const FilterContainer = Styled.View<{index: number}>`
  z-index: ${({index}) => 100 - index};
`;

const FiltersContainer = Styled.View<{responsive: ResponsiveType}>`
  padding-horizontal: ${({responsive}) => (responsive.desktop ? 24 : 16)}px;
`;

type LeadParamsType = {
  moveStartDate?: string;
  moveEndDate?: string;
  teamMemberIds?: string[];
};
type SetLeadParamsType = SetStateType<LeadParamsType>;

const handleSaveParamsToUrl = ({
  urlFilters,
  leadParams,
}: {
  urlFilters: SalesLeadsFiltersType;
  leadParams: LeadParamsType;
}) => {
  urlFilters.handleUpdate({
    moveStartDate: leadParams.moveStartDate,
    moveEndDate: leadParams.moveEndDate,
    teamMemberIds: leadParams.teamMemberIds,
    // Always reset to page 1 when filters are applied
    page: 1,
  });
};

const handleClearFilters = ({
  urlFilters,
  setLeadParams,
}: {
  urlFilters: SalesLeadsFiltersType;
  setLeadParams: SetLeadParamsType;
}) => {
  const clearedFilters = {
    ...urlFilters.getFilters(),
    moveStartDate: '',
    moveEndDate: '',
    teamMemberIds: [],
  };
  urlFilters.handleUpdate({
    ...clearedFilters,
  });
  setLeadParams({...clearedFilters});
};

interface DateFilterProps {
  index: number;
  dateKey: 'moveStartDate' | 'moveEndDate';
  label?: string;
  position: string;
  setLeadParams: SetLeadParamsType;
  leadParams: LeadParamsType;
}

const DateFilter = ({
  index,
  dateKey,
  label,
  position,
  setLeadParams,
  leadParams,
}: DateFilterProps) => {
  const responsive = useResponsive();
  return (
    <RowFilterInputContainer>
      <FilterContainer index={index}>
        {label ? <Text.Body>{label}</Text.Body> : <Space height={20} />}
        <FieldInput.Spacer isResponsive />
        <DateInput
          placeholder={'MM/DD/YYYY'}
          setFieldValue={() => {}}
          onChangeDate={(date: string | null) =>
            setLeadParams({...leadParams, [dateKey]: date ? Datetime.convertToDate(date) : ''})
          }
          value={Datetime.toFormDate(leadParams[dateKey])}
          style={{width: '100%'}}
          position={position}
        />
      </FilterContainer>
    </RowFilterInputContainer>
  );
};

interface LeadListFilterFieldsProps {
  viewer?: UserModel;
  leadParams: LeadParamsType;
  setLeadParams: SetLeadParamsType;
  urlFilters: SalesLeadsFiltersType;
}

const LeadListFilterFields = ({
  viewer,
  leadParams,
  setLeadParams,
  urlFilters,
}: LeadListFilterFieldsProps) => {
  const userKind = urlFilters.get('userKind');
  const teamMemberDropdownOptions = viewer?.viewingOrganization
    ? Organization.getAllOfficeStaffOptions(viewer.viewingOrganization)
    : [];

  // Prepend the option "None" to the dropdown
  teamMemberDropdownOptions.unshift({
    // @ts-expect-error Needs updated types in MultiDropdownCheckboxInput
    value: null,
    label: 'None',
    description: 'Show leads without an assigned salesperson',
  });

  return (
    <React.Fragment>
      <Section index={0}>
        <Row>
          <DateFilter
            index={1}
            dateKey={'moveStartDate'}
            label={'Move Date Range'}
            position={DateInput.Positions.BOTTOM_START}
            setLeadParams={setLeadParams}
            leadParams={leadParams}
          />
          <Space width={16} />
          <DateFilter
            index={2}
            dateKey={'moveEndDate'}
            position={DateInput.Positions.BOTTOM_END}
            setLeadParams={setLeadParams}
            leadParams={leadParams}
          />
        </Row>
      </Section>
      {userKind === SalesLeadsTabKinds.COMPANY && (
        <React.Fragment>
          <Space height={16} />
          {viewer && UserRole.ADMIN_ROLES_PLUS_SUPER.includes(viewer.salesAppRole) && (
            <Section index={4}>
              <Text.Body>Salespersons</Text.Body>
              <FieldInput.Spacer isResponsive />
              <MultiDropdownCheckboxInput
                options={teamMemberDropdownOptions}
                placeholder={'Select salespersons'}
                style={{flex: 1}}
                value={_.get(leadParams, 'teamMemberIds', [])}
                onChangeValue={(selectedMembers) => {
                  // @ts-expect-error Needs updated types in MultiDropdownCheckboxInput
                  setLeadParams({...leadParams, teamMemberIds: selectedMembers});
                }}
                isSearchable
                isEnabledSelectAll
                isDescriptionBelow
                isResponsive
                usePills
              />
            </Section>
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

interface LeadListFiltersContentProps {
  viewer?: UserModel;
  urlFilters: SalesLeadsFiltersType;
}

const LeadListFiltersContent = ({viewer, urlFilters}: LeadListFiltersContentProps) => {
  const responsive = useResponsive();
  const leadListFiltersPopover = usePopover({name: 'Lead List Filters Popover'});
  const [leadParams, setLeadParams] = useState<LeadParamsType>({
    ...urlFilters.getFilters(),
  });
  const countFilterKeys: (keyof SalesLeadsFilterKeys)[] = [
    'moveStartDate',
    'moveEndDate',
    'teamMemberIds',
  ];

  const handleSetUrlParams = () => {
    return handleSaveParamsToUrl({urlFilters, leadParams});
  };

  return (
    <Container responsive={responsive}>
      <Popover.Content innerRef={leadListFiltersPopover.ref}>
        <SecondaryButton
          isResponsive
          text={urlFilters.getFilterCountLabel({
            filterKeys: countFilterKeys,
            isResponsive: true,
          })}
          iconLeft={Icon.Filter}
          onPress={leadListFiltersPopover.handleToggle}
          isWidthOfContainer
        />
      </Popover.Content>
      <PopoverFilterWithFooter
        activeFiltersCount={urlFilters.getFilterCount({
          filterKeys: countFilterKeys,
        })}
        handleClear={() => {
          handleClearFilters({urlFilters, setLeadParams});
        }}
        handleApply={() => {
          handleSetUrlParams();
          leadListFiltersPopover.handleClose();
        }}
        popover={leadListFiltersPopover}
      >
        <LeadListFilterFields
          viewer={viewer}
          leadParams={leadParams}
          setLeadParams={setLeadParams}
          urlFilters={urlFilters}
        />
        <Space height={16} />
      </PopoverFilterWithFooter>
    </Container>
  );
};

interface SalesLeadsFiltersProps {
  viewer?: UserModel;
  urlFilters: SalesLeadsFiltersType;
}

const SalesLeadsFilters = ({viewer, urlFilters}: SalesLeadsFiltersProps) => {
  const responsive = useResponsive();
  const [searchValue, setSearchValue] = useState(urlFilters.get('searchQuery') || '');

  // Reset input value if a top level navigation resets the searchQuery
  useEffect(() => {
    if (!urlFilters.get('searchQuery')) {
      setSearchValue('');
    }
  }, [urlFilters.get('searchQuery')]);

  const handleChangeText = useDebouncedCallback(
    (query) =>
      urlFilters.handleUpdate({
        searchQuery: searchValue,
        page: 1,
      }),
    750,
  );

  const handleInputChange = (text: string) => {
    setSearchValue(text);
    handleChangeText(text);
  };

  const isBoardView = urlFilters.get('view') === 'BOARD';

  return (
    <React.Fragment>
      <FiltersContainer responsive={responsive}>
        <Row>
          <SearchBar
            placeholder={responsive.desktop ? 'Search name, phone number, email address' : 'Search'}
            onChangeText={handleInputChange}
            containerStyle={{flex: 1}}
            style={{flex: 1}}
            isResponsive
            valueOverride={searchValue}
          />
          <Space width={16} />
          <LeadListFiltersContent viewer={viewer} urlFilters={urlFilters} />
          {responsive.desktop && viewer?.viewingOrganization.features.isEnabledLeadsBoardView && (
            <React.Fragment>
              <Space width={16} />
              <ContextSwitcher
                contextDefinitions={[
                  {
                    iconSource: Icon.ChartKanban,
                    isSelected: isBoardView,
                    onPress: () => urlFilters.handleUpdate({view: 'BOARD'}),
                  },
                  {
                    iconSource: Icon.Bars,
                    isSelected: !isBoardView,
                    onPress: () => urlFilters.handleUpdate({view: undefined}),
                  },
                ]}
              />
            </React.Fragment>
          )}
        </Row>
      </FiltersContainer>
    </React.Fragment>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
SalesLeadsFilters.fragment = gql`
  ${Organization.getAllOfficeStaffOptions.fragment}
  fragment SalesLeadsFilters on User {
    id
    salesAppRole
    viewingOrganization {
      id
      features {
        isEnabledLeadsBoardView: isEnabled(feature: "LEADS_BOARD_VIEW")
      }
      ...Organization_getAllOfficeStaffOptions
    }
  }
`;

export default SalesLeadsFilters;
